import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import WfLoader from '@app/src/components/WfLoader'
import type { Response } from '@app/src/types/resourceExplorer'
import { uniq } from '@app/src/utils/helpers'
import { ExpandMoreOutlined, InfoOutlined } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Chip,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface ReportedDataFoundStepProps {
  alreadyExistingReportedData: Response[]
  alreadyExistingReportedDataLoading: boolean
}

const ReportedDataFoundStep: React.FC<ReportedDataFoundStepProps> = ({
  alreadyExistingReportedData,
  alreadyExistingReportedDataLoading,
}) => {
  const { formatMessage } = useIntl()

  if (alreadyExistingReportedDataLoading) return <WfLoader />

  const responsesByProviderId = Object.values(
    Object.groupBy(alreadyExistingReportedData ?? [], g => g.request.subscriptions?.[0]?.targetProviderId),
  ).map(s => s ?? [])

  return (
    <Accordion
      elevation={0}
      disableGutters
      sx={[{ bgcolor: 'grey.100', '&:before': { position: 'unset' } }]}
      defaultExpanded
    >
      <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />} sx={{ py: 1 }}>
        <Stack direction="row">
          <InfoOutlined />
          <Typography variant="body1" ml={3}>
            {formatMessage({ id: 'statistics.companiesCount' }, { count: responsesByProviderId.length })}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {responsesByProviderId.map(responses => {
            const provider = responses?.[0].request.subscriptions?.[0].target
            const uniqueTemplatesNames = uniq(responses?.map(res => res.request.title) ?? [])
            return (
              <ListItem
                key={provider.id}
                sx={{
                  py: 2,
                  borderTop: `1px solid ${alpha('#000', 0.12)}`,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} width="60%">
                  <CompanyCell company={provider} disableCell sx={{ width: '60%', minWidth: 0 }} />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Chip label={uniqueTemplatesNames[0]} />
                  {uniqueTemplatesNames.length > 1 && (
                    <Tooltip title={uniqueTemplatesNames.slice(1).join(', ')}>
                      <Chip label={`+ ${uniqueTemplatesNames.length - 1}`} />
                    </Tooltip>
                  )}
                </Stack>
              </ListItem>
            )
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

export default ReportedDataFoundStep
